<template>
  <div class="bg-slate-50 min-h-screen p-4 md:p-8 max-w-5xl mx-auto">
    <!-- Header -->
    <header class="mb-8 flex items-center gap-3">
      <div class="bg-emerald-600 p-2 rounded-lg text-white shadow-lg shadow-emerald-200">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 6l9 3 9-3-9-3-9 3z"></path>
          <path d="M3 12l9 3 9-3-9-3-9 3z"></path>
          <path d="M3 18l9 3 9-3-9-3-9 3z"></path>
        </svg>
      </div>
      <div>
        <h1 class="text-2xl font-bold text-slate-900">数据库索引清理助手 (Vue 3)</h1>
        <p class="text-slate-500 text-sm font-medium">先清理重复数据，再安全创建唯一索引</p>
      </div>
    </header>

    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <!-- Left Config Panel -->
      <div class="lg:col-span-4 space-y-6">
        <div class="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <h2 class="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <span>配置选项</span>
            <div class="h-px flex-1 bg-slate-100"></div>
          </h2>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-semibold text-slate-700 mb-1">表名称 (Table ID)</label>
              <input
                v-model="config.tableId"
                type="text"
                placeholder="例如: sys_user_logs"
                class="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none transition-all"
              />
            </div>
            <div>
              <label class="block text-sm font-semibold text-slate-700 mb-1">唯一键字段 (Column ID)</label>
              <input
                v-model="config.columnId"
                type="text"
                placeholder="例如: request_id"
                class="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none transition-all"
              />
            </div>

            <div class="pt-2">
              <button
                :disabled="isRunning"
                @click="startProcess"
                class="w-full py-3 px-4 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white rounded-xl font-bold transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2"
              >
                <template v-if="!isRunning">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 11 12 14 22 4"></polyline>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                  </svg>
                  执行自动化任务
                </template>
                <template v-else>
                  <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  正在处理...
                </template>
              </button>
            </div>
          </div>
        </div>

        <!-- Step Tracker -->
        <div v-if="isRunning || isCompleted" class="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <h3 class="text-sm font-bold text-slate-900 mb-4">当前进度</h3>
          <div class="space-y-4">
            <div v-for="(step, index) in steps" :key="index" class="flex items-center gap-3">
              <div class="relative">
                <div v-if="currentStep > index" class="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div v-else-if="currentStep === index" class="w-6 h-6 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin"></div>
                <div v-else class="w-6 h-6 rounded-full border-2 border-slate-200"></div>
              </div>
              <span :class="['text-sm', currentStep === index ? 'font-bold text-emerald-600' : 'text-slate-500']">
                {{ step }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Log Panel -->
      <div class="lg:col-span-8">
        <div class="bg-[#0f172a] rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[600px] border border-slate-800">
          <div class="bg-slate-800/50 px-5 py-3 border-b border-slate-700 flex justify-between items-center">
            <div class="flex items-center gap-2">
              <div class="flex gap-1.5 mr-2">
                <div class="w-3 h-3 rounded-full bg-rose-500"></div>
                <div class="w-3 h-3 rounded-full bg-amber-500"></div>
                <div class="w-3 h-3 rounded-full bg-emerald-500"></div>
              </div>
              <span class="text-slate-400 text-xs font-mono uppercase tracking-[0.2em]">System Console</span>
            </div>
            <button type="button" class="text-slate-500 hover:text-slate-300 text-xs transition-colors" @click="logs = []">
              清空日志
            </button>
          </div>

          <div class="flex-1 overflow-y-auto p-6 font-mono text-[13px] leading-relaxed index-cleanup-log">
            <div v-if="logs.length === 0" class="text-slate-600 italic">
              > 准备就绪，请输入参数并开始任务...
            </div>
            <transition-group name="log">
              <div v-for="log in logs" :key="log.id" class="flex gap-4 mb-2">
                <span class="text-slate-500 shrink-0 select-none">[{{ log.time }}]</span>
                <span :class="logClass(log.type)">{{ log.content }}</span>
              </div>
            </transition-group>
          </div>

          <!-- Result Summary -->
          <div v-if="isCompleted" class="bg-emerald-500/10 p-5 border-t border-emerald-500/20">
            <div class="flex items-center gap-3 text-emerald-400 font-bold mb-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              操作执行成功
            </div>
            <p class="text-slate-400 text-sm ml-8">
              表 <span class="text-slate-200">"{{ config.tableId }}"</span> 已完成清理并成功创建唯一索引。
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- 确认执行清理弹窗 -->
    <Teleport to="body">
      <Transition name="modal">
        <div
          v-if="showConfirmModal"
          class="fixed inset-0 z-[100] flex items-center justify-center p-4"
          @click.self="cancelCleanup"
        >
          <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" aria-hidden="true" />
          <div
            class="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
          >
            <div class="p-6 pb-4">
              <div class="flex items-start gap-4">
                <div class="shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h2 id="confirm-title" class="text-lg font-bold text-slate-900 mb-1">确认执行清理</h2>
                  <p class="text-slate-600 text-sm leading-relaxed">
                    {{ confirmMessage }}
                  </p>
                  <p v-if="confirmTotalDup > 0" class="mt-2 text-amber-600 text-sm font-medium">
                    将删除 {{ confirmTotalDup }} 条重复记录，此操作不可恢复。
                  </p>
                  <div
                    v-if="duplicatePreview.length"
                    class="mt-3 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                  >
                    <p class="text-xs text-slate-500 mb-1">
                      重复值预览（前 {{ duplicatePreview.length }} 个）：
                    </p>
                    <ul class="text-xs text-slate-700 space-y-1">
                      <li
                        v-for="(val, idx) in duplicatePreview"
                        :key="idx"
                        class="flex justify-between gap-2"
                      >
                        <span class="text-slate-400 shrink-0">#{{ idx + 1 }}</span>
                        <span class="font-mono break-all text-right flex-1">{{ val }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div class="px-6 py-4 bg-slate-50 flex justify-end gap-3">
              <button
                type="button"
                @click="cancelCleanup"
                class="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
              >
                取消
              </button>
              <button
                type="button"
                @click="confirmCleanup"
                class="px-4 py-2.5 rounded-xl text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
              >
                确定执行
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { scanDuplicates, cleanDuplicates } from '@/api/indexCleanup.js'

export default {
  name: 'IndexCleanup',
  setup() {
    const config = reactive({
      tableId: '',
      columnId: ''
    })

    const isRunning = ref(false)
    const isCompleted = ref(false)
    const currentStep = ref(-1)
    const logs = ref([])
    const showConfirmModal = ref(false)
    const confirmMessage = ref('')
    const confirmTotalDup = ref(0)
    const duplicatePreview = ref([])
    const pendingParams = ref(null)

    const steps = [
      '数据库连通性检查',
      '查找重复记录 (Group By)',
      '执行清理脚本 (保留最小ID)',
      '创建唯一性约束 (Unique Index)'
    ]

    const addLog = (content, type = 'info') => {
      logs.value.unshift({
        id: Date.now() + Math.random(),
        time: new Date().toLocaleTimeString(),
        content,
        type
      })
    }

    const logClass = (type) => ({
      'text-emerald-400': type === 'success',
      'text-amber-400': type === 'warning',
      'text-rose-400': type === 'error',
      'text-slate-300': type === 'info',
      'text-blue-400': type === 'system'
    })

    const startProcess = async () => {
      if (!config.tableId || !config.columnId) {
        alert('请填写表 ID 和字段 ID')
        return
      }

      isRunning.value = true
      isCompleted.value = false
      logs.value = []
      currentStep.value = 0

      const params = { tableId: config.tableId.trim(), columnId: config.columnId.trim() }

      try {
        // 步骤 1：数据库连通性检查 + 扫描重复
        addLog(`> 开始任务：针对表 [${params.tableId}] 优化索引`, 'system')
        addLog('正在验证数据库及表结构并扫描重复记录...', 'info')

        const scanRes = await scanDuplicates(params)

        // 兼容两种返回：{ status, data } 或 直接 { total_duplicates, preview }
        const scanData = scanRes.data ?? scanRes
        const scanOk = scanRes.status === 'success' || (scanData && typeof scanData.total_duplicates === 'number')
        if (!scanOk) {
          addLog(scanRes.detail || scanRes.error || '扫描失败', 'error')
          return
        }
        addLog(`表结构验证通过，字段 [${params.columnId}] 存在。`, 'success')

        // 步骤 2：查找重复记录（已完成）
        currentStep.value = 1
        const totalDup = scanData.total_duplicates ?? 0
        duplicatePreview.value = Array.isArray(scanData.duplicate_values_preview)
          ? scanData.duplicate_values_preview
          : []
        addLog(`重复项扫描完成，共 ${totalDup} 条待清理。`, totalDup > 0 ? 'warning' : 'info')

        // 执行清理前弹出确认
        confirmMessage.value = totalDup > 0
          ? `扫描完成：共发现 ${totalDup} 条重复记录。将保留每组 _id 最小的一条，其余删除，并创建唯一索引。`
          : `当前表内无重复记录。将直接为字段创建唯一索引。`
        confirmTotalDup.value = totalDup
        pendingParams.value = params
        showConfirmModal.value = true
        return
      } catch (err) {
        addLog(`任务中止：${err.message}`, 'error')
      } finally {
        if (!showConfirmModal.value) isRunning.value = false
      }
    }

    const cancelCleanup = () => {
      showConfirmModal.value = false
      pendingParams.value = null
      duplicatePreview.value = []
      addLog('用户取消执行清理。', 'info')
      isRunning.value = false
    }

    const confirmCleanup = async () => {
      const params = pendingParams.value
      showConfirmModal.value = false
      pendingParams.value = null
      duplicatePreview.value = []
      if (!params) return

      try {
        // 步骤 3：执行清理
        currentStep.value = 2
        addLog('正在执行清理：保留每组最小 _id，删除其余重复行...', 'info')

        const cleanRes = await cleanDuplicates(params)

        // 兼容两种返回：{ status, data } 或 直接 { deleted_count, unique_index }
        const cleanData = cleanRes.data ?? cleanRes
        const cleanOk = cleanRes.status === 'success' || (cleanData && typeof cleanData.deleted_count === 'number')
        if (!cleanOk) {
          addLog(cleanRes.detail || '清理或创建索引失败', 'error')
          return
        }
        const deleted = cleanData.deleted_count ?? 0
        const idxName = cleanData.unique_index ?? params.columnId
        addLog(`清理完成，共删除 ${deleted} 条重复记录。`, 'success')
        addLog(`唯一索引已创建：${idxName}`, 'success')

        // 步骤 4：创建唯一索引（后端已在 cleanup 中完成）
        currentStep.value = 3
        currentStep.value = 4
        isCompleted.value = true
        addLog(`任务于 ${new Date().toLocaleTimeString()} 顺利结束。`, 'system')
      } catch (err) {
        addLog(`任务中止：${err.message}`, 'error')
      } finally {
        isRunning.value = false
      }
    }

    return {
      config,
      isRunning,
      isCompleted,
      currentStep,
      steps,
      logs,
      showConfirmModal,
      confirmMessage,
      confirmTotalDup,
      duplicatePreview,
      startProcess,
      cancelCleanup,
      confirmCleanup,
      logClass
    }
  }
}
</script>

<style scoped>
.index-cleanup-log::-webkit-scrollbar {
  width: 6px;
}
.index-cleanup-log::-webkit-scrollbar-thumb {
  background: #334155;
  border-radius: 3px;
}
</style>

<style>
/* 日志列表过渡动画，需非 scoped 以作用于 transition-group 子元素 */
.log-enter-active,
.log-leave-active {
  transition: all 0.3s ease;
}
.log-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

/* 确认弹窗过渡 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.96);
}
</style>
